
import { useState, useMemo } from 'react';
import { Transaction } from "@/types/payments";

export function useTransactionSort(filteredTransactions: Transaction[]) {
  const [sortKey, setSortKey] = useState<keyof Transaction | null>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Sort transactions based on sort key and direction
  const sortedTransactions = useMemo(() => {
    if (!sortKey) return filteredTransactions;
    
    return [...filteredTransactions].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];
      
      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === 'asc' ? -1 : 1;
      if (bValue == null) return sortDirection === 'asc' ? 1 : -1;
      
      // Handle date fields
      if (sortKey === 'date') {
        const aDate = new Date(aValue as string).getTime();
        const bDate = new Date(bValue as string).getTime();
        return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }
      
      // Handle amount (number)
      if (sortKey === 'amount') {
        const aNum = Number(aValue);
        const bNum = Number(bValue);
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }
      
      // Handle string fields (description, currency, type, category)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      
      // Default comparison for other types
      const aStr = String(aValue);
      const bStr = String(bValue);
      const comparison = aStr.localeCompare(bStr);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredTransactions, sortKey, sortDirection]);

  // Handle sorting when a column header is clicked
  const handleSort = (key: keyof Transaction) => {
    setSortDirection(prevDirection => 
      sortKey === key && prevDirection === 'asc' ? 'desc' : 'asc'
    );
    setSortKey(key);
  };

  // Render sort icon - return string representation instead of JSX
  const renderSortIcon = (key: keyof Transaction) => {
    if (sortKey !== key) return null;
    return sortDirection === 'asc' ? "↑" : "↓";
  };

  return {
    sortKey,
    sortDirection,
    sortedTransactions,
    handleSort,
    renderSortIcon
  };
}
